/* eslint-disable @typescript-eslint/no-empty-function */
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'
import { Box, DashboardCard, P } from 'styling/GlobalStyles'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { IWidget } from 'types/dashboard.interface'
import { TitleText } from 'styling/Typography'
import { WidgetEditOverlay } from 'pages/Dashboard/Dashboard.styled'
import { RootState } from 'store'

/** Fallback when `accommodationSummary` is missing (matches design mock) */
const FALLBACK_CAPACITY = 200
const FALLBACK_OCCUPIED = 164
const FALLBACK_AVAILABLE = 36

const OCCUPIED_GREEN = '#22c55e'
const AVAILABLE_CYAN = '#38bdf8'
const CHART_ORANGE = '#ff9900'

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const ManageLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colorOrange};
  cursor: pointer;

  &:disabled {
    cursor: inherit;
    opacity: 0.85;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  align-items: center;
  gap: 1rem;
`

const ChartWrap = styled.div`
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  aspect-ratio: 1 / 1;
  max-width: 40%;
  align-self: center;
  container-type: size;
  container-name: accommodation-chart;
`

const CenterLabel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  text-align: center;
`

const CenterPct = styled.span`
  font-size: clamp(11px, 18cqmin, 30px);
  font-weight: 700;
  line-height: 1;
  color: ${({ theme }) => theme.textColor};
`

const CenterSub = styled.span`
  font-size: clamp(10px, 8cqmin, 13px);
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.greyText};
  margin-top: clamp(1px, 1.5cqmin, 4px);
`

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  width: 100%;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 0.75rem;

  .meta-label {
    color: ${({ theme }) => theme.greyText};
    font-weight: 500;
  }
`

const MetaRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;
`

const StatCard = styled(Box) <{ $accent?: 'green' | 'blue' | 'gray' }>`
  border-radius: 12px;
  padding: 10px 8px 10px 11px;
  border-left: 1.5px solid transparent;
  ${(p) => p.$accent === 'green' && `border-left-color: ${OCCUPIED_GREEN};`}
  ${(p) => p.$accent === 'blue' && `border-left-color: ${AVAILABLE_CYAN};`}
  ${(p) => p.$accent === 'gray' && 'border-left-color: #8A8A8A;'}
`

const StatValue = styled.div<{ $variant?: 'default' | 'green' | 'cyan' }>`
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
  text-align:  center;
  color: ${({ theme, $variant }) => {
    if ($variant === 'green') {
      return OCCUPIED_GREEN
    }
    if ($variant === 'cyan') {
      return AVAILABLE_CYAN
    }
    return theme.textColor
  }};
`

const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
    text-align: center;
  color: ${({ theme }) => theme.greyText};
  margin-top: 6px;
`


export const HrAccommodationWidget = ({
  modalOpen,
  removeWidget,
  onDragOver,
  onDragStart,
  onDragDrop,
}: IWidget) => {
  const { accommodationSummary } = useSelector((state: RootState) => state.hrDashboard)
  const { darkMode } = useSelector((state: RootState) => state.globals)
  const navigate = useNavigate()

  const { totalCapacity, occupied, available, dirtyRooms, guestStays, occupancyPct, pieData } = useMemo(() => {
    const s = accommodationSummary
    const totalCapacity = s?.totalCapacity ?? FALLBACK_CAPACITY
    const occupied = s?.occupied ?? FALLBACK_OCCUPIED
    const available = s?.available ?? FALLBACK_AVAILABLE
    const dirtyRooms = s?.dirtyRooms ?? 0
    const guestStays = s?.guestStays ?? 0

    const pct =
      s?.occupancyRate != null
        ? Math.min(100, Math.max(0, Math.round(s.occupancyRate)))
        : totalCapacity > 0
          ? Math.min(100, Math.max(0, Math.round((occupied / totalCapacity) * 100)))
          : 0

    const trackColor = darkMode ? '#4a4a4a' : '#d4d4d4'

    const pieData =
      pct >= 100
        ? [{ name: 'full', value: 100, color: CHART_ORANGE }]
        : pct <= 0
          ? [{ name: 'track', value: 100, color: trackColor }]
          : [
            { name: 'occ', value: pct, color: CHART_ORANGE },
            { name: 'rem', value: 100 - pct, color: trackColor },
          ]

    return { totalCapacity, occupied, available, dirtyRooms, guestStays, occupancyPct: pct, pieData }
  }, [accommodationSummary, darkMode])

  return (
    <DashboardCard
      draggable={modalOpen}
      onDragStartCapture={() => {
        onDragStart('accommodation', 1)
      }}
      onDrop={onDragDrop}
      onDragEnd={(e) => {
        e.preventDefault()
      }}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver('accommodation')
      }}
      width='1fr'
      style={{ gridArea: 'accommodation', cursor: modalOpen ? 'grab' : 'normal' }}
    >
      {modalOpen && (
        <WidgetEditOverlay
          onClick={(e) => {
            removeWidget('accommodation')
            e.stopPropagation()
          }}
        >
          <P>Remove Widget</P>
          <CloseIcon />
        </WidgetEditOverlay>
      )}
      <CardHeader>
        <TitleText weight='bold' style={{ margin: 0, flex: 1, minWidth: 0 }}>
          ACCOMMODATION
        </TitleText>
        <ManageLink
          type='button'
          disabled={modalOpen}
          onClick={(e) => {
            e.stopPropagation()
            if (!modalOpen) {
              navigate('/hr/accommodation')
            }
          }}
        >
          Manage
          <span aria-hidden='true'>{'>'}</span>
        </ManageLink>
      </CardHeader>
      <Content>
        <ChartWrap>
          <ResponsiveContainer width='100%' height='100%'>
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Pie
                data={pieData}
                dataKey='value'
                cx='50%'
                cy='50%'
                innerRadius='84%'
                outerRadius='100%'
                startAngle={90}
                endAngle={-270}
                paddingAngle={0}
                cornerRadius={8}
                stroke='none'
                isAnimationActive={false}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`${entry.name}-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <CenterLabel>
            <CenterPct>{occupancyPct}%</CenterPct>
            <CenterSub>OCCUPANCY</CenterSub>
          </CenterLabel>
        </ChartWrap>

        <StatGrid>
          <StatCard $accent='gray'>
            <StatValue>{totalCapacity}</StatValue>
            <StatLabel>CAPACITY</StatLabel>
          </StatCard>
          <StatCard $accent='green'>
            <StatValue $variant='green'>{occupied}</StatValue>
            <StatLabel>OCCUPIED</StatLabel>
          </StatCard>
          <StatCard $accent='blue'>
            <StatValue $variant='cyan'>{available}</StatValue>
            <StatLabel>AVAILABLE</StatLabel>
          </StatCard>
        </StatGrid>
        <MetaRows>
          <MetaRow>
            <span className='meta-label'>Dirty Rooms</span>
            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: CHART_ORANGE }}>{dirtyRooms}</span>
          </MetaRow>
          <MetaRow>
            <span className='meta-label'>Guest Stays</span>
            <StatValue $variant='cyan' style={{ fontSize: '0.875rem' }}>{guestStays}</StatValue>
          </MetaRow>
        </MetaRows>

      </Content>
    </DashboardCard>
  )
}
